import { difference } from 'lodash';
import { FilterResultCounts } from '../types';
import { FilterTypes } from '../constants/initialFilters';
import {
  AnalyticsFilters,
  VTPDataLayerEvent,
  Event,
  InitialStateEventBody,
  FiltersClearEventBody,
  FilterClearEventBody,
  FilterPanelCancelEventBody,
  FiltersSaveEventBody,
  FilterPanelChangeEventBody,
  RangeFilterState,
  RangeSliderChangeEventComponent,
  RadioChangeEventComponent,
  MultipleSelectChangeEventComponent,
  FilterPanelChangeEventComponent,
  CurrentStateEventBody,
  ResultsReturnedEventBody,
  ErrorBody,
  ResultsReturnedFailEventBody,
  Tier3DealersEventBody,
} from '../types/analytics';

type FiltersChangeSender = (prevState: FilterTypes, nextState: FilterTypes) => void;
type FiltersSnapshotSender = (filters: FilterTypes, totalCount?: number | null) => void;
type FilterClearSender = (prevState: FilterTypes, removedFilter: string) => void;
type SuccessResultSender = (totalCount: number | null) => void;
type ResultsFailedSender = (result?: ErrorBody) => void;
type Tier3DealersListSender = (configFile: any) => void;

// extend window interface so we can add a global
declare global {
  interface Window {
    ixisDataLayer: VTPDataLayerEvent[];
  }
}
window.ixisDataLayer = window.ixisDataLayer || [];

const clone = (obj: any): any => JSON.parse(JSON.stringify(obj));

const convertToRangeFilterState = ([min, max]: unknown[]): RangeFilterState =>
  typeof min === 'number' && typeof max === 'number' ? { min, max } : null;

const rangeFilters = ['price', 'modelYear', 'mileage', 'power'] as const;
type RangeFilters = Extract<keyof FilterTypes, (typeof rangeFilters)[number]>;
type AnalyticsRangeFilters = Pick<AnalyticsFilters, RangeFilters>;

const convertAllRangeFilters = (filters: FilterTypes): AnalyticsRangeFilters => {
  return rangeFilters.reduce<AnalyticsRangeFilters>(
    (accumulator, rangeFilterKey) => ({
      ...accumulator,
      [rangeFilterKey]: convertToRangeFilterState(filters[rangeFilterKey]),
    }),
    {} as AnalyticsRangeFilters, // reduce returns Partial<T> during iteration
  );
};

export const filtersChange: FiltersChangeSender = (prevState, nextState) => {
  const changedKeys: Array<keyof FilterTypes> = (
    Object.keys(prevState) as Array<keyof FilterTypes>
  ).filter(
    // Since we are never mutating arrays and objects,
    // a shallow compare will show if the filter has changed
    (key) => prevState[key] !== nextState[key],
  );

  const events: FilterPanelChangeEventComponent[] = [];
  changedKeys.forEach((key) => {
    if (!Array.isArray(prevState[key]) && !Array.isArray(nextState[key])) {
      const shouldChangeToString = key === 'availableInMonths';
      // Change of a radio button
      const eventComponent: RadioChangeEventComponent = {
        action: 'change',
        filterName: key,
        prevValue: shouldChangeToString
          ? (prevState[key]?.toString() ?? null)
          : (prevState[key] as string | number | null),
        value: shouldChangeToString
          ? (nextState[key]?.toString() ?? null)
          : (nextState[key] as string | number | null),
      };

      events.push(eventComponent);
      return;
    }

    const added = difference<string | number>(
      nextState[key] as string[] | number[],
      prevState[key] as string[] | number[],
    );
    const removed = difference<string | number>(
      prevState[key] as string[] | number[],
      nextState[key] as string[] | number[],
    );

    type NumericKey = Extract<FilterTypes, number[]>;
    const isNumeric = (filterKey: keyof FilterTypes): filterKey is NumericKey =>
      typeof [...added, ...removed][0] === 'number';

    if (isNumeric(key)) {
      // change of range slider

      const prevValue = convertToRangeFilterState(prevState[key]);

      const nextValue = convertToRangeFilterState(nextState[key]);

      const eventComponent: RangeSliderChangeEventComponent = {
        action: 'change',
        filterName: key,
        prevValue,
        value: nextValue,
      };

      events.push(eventComponent);
      return;
    }

    // change of a multiple-select filter
    added.forEach((element) => {
      if (typeof element === 'string') {
        const eventComponent: MultipleSelectChangeEventComponent = {
          action: 'select',
          filterName: key,
          value: element,
        };

        events.push(eventComponent);
      }
    });

    removed.forEach((element) => {
      if (typeof element === 'string') {
        const eventComponent: MultipleSelectChangeEventComponent = {
          action: 'deselect',
          filterName: key,
          value: element,
        };
        events.push(eventComponent);
      }
    });
  });

  const event: Event<FilterPanelChangeEventBody> = {
    vtp: {
      type: 'filter_panel_change',
      payload: events,
    },
  };
  window.ixisDataLayer.push(clone(event));
};

export const filtersSave: FiltersSnapshotSender = (filters, totalCount) => {
  const event: Event<FiltersSaveEventBody> = {
    vtp: {
      type: 'filter_panel_save',
      payload: {
        ...filters,
        usedInventoryType: filters.vehicleType === 'new' ? null : filters.usedInventoryType,
        availableInMonths:
          filters?.vehicleType === 'new' ? (filters.availableInMonths?.toString() ?? null) : null,
        ...convertAllRangeFilters(filters),
      },
      filterResults: {
        totalCount: totalCount?.toString(),
      },
    },
  };
  window.ixisDataLayer.push(clone(event));
};

export const filtersCancel: FiltersSnapshotSender = (filters) => {
  const event: Event<FilterPanelCancelEventBody> = {
    vtp: {
      type: 'filter_panel_cancel',
      payload: {
        ...filters,
        availableInMonths: filters.availableInMonths?.toString() ?? null,
        usedInventoryType: filters.vehicleType === 'new' ? null : filters.usedInventoryType,
        ...convertAllRangeFilters(filters),
      },
    },
  };
  window.ixisDataLayer.push(clone(event));
};

export const filtersClear: FiltersSnapshotSender = (prevState) => {
  const event: Event<FiltersClearEventBody> = {
    vtp: {
      type: 'filter_clear_all',
      payload: {
        ...prevState,
        availableInMonths: prevState.availableInMonths?.toString() ?? null,
        usedInventoryType: prevState.vehicleType === 'new' ? null : prevState.usedInventoryType,
        ...convertAllRangeFilters(prevState),
      },
    },
  };
  window.ixisDataLayer.push(clone(event));
};

export const filterClear: FilterClearSender = (prevState, removedFilter) => {
  const event: Event<FilterClearEventBody> = {
    vtp: {
      type: 'filter_clear_individual',
      payload: {
        ...prevState,
        availableInMonths: prevState.availableInMonths?.toString() ?? null,
        usedInventoryType: prevState.vehicleType === 'new' ? null : prevState.usedInventoryType,
        ...convertAllRangeFilters(prevState),
      },
      removedFilter,
    },
  };
  window.ixisDataLayer.push(clone(event));
};

export const filterInitialState: FiltersSnapshotSender = (filters) => {
  const event: Event<InitialStateEventBody> = {
    vtp: {
      type: 'filter_initial_state',
      payload: {
        ...filters,
        availableInMonths: filters.availableInMonths?.toString() ?? null,
        usedInventoryType: filters.vehicleType === 'new' ? null : filters.usedInventoryType,
        ...convertAllRangeFilters(filters),
      },
    },
  };

  window.ixisDataLayer.push(clone(event));
};

export const filterCurrentState: FiltersSnapshotSender = (filters) => {
  const event: Event<CurrentStateEventBody> = {
    vtp: {
      type: 'filter_state',
      payload: {
        ...filters,
        availableInMonths: filters.availableInMonths?.toString() ?? null,
        usedInventoryType: filters.vehicleType === 'new' ? null : filters.usedInventoryType,
        ...convertAllRangeFilters(filters),
      },
    },
  };

  window.ixisDataLayer.push(clone(event));
};

export const resultsReturnedEvent: SuccessResultSender = (filterResults) => {
  const event: Event<ResultsReturnedEventBody> = {
    vtp: {
      type: 'results_returned',
      payload: {
        totalCount: filterResults ? filterResults.toString() : null,
      },
    },
  };

  window.ixisDataLayer.push(clone(event));
};

export const resultsReturnedFailed: ResultsFailedSender = (failedResults) => {
  const event: Event<ResultsReturnedFailEventBody> = {
    vtp: {
      type: 'results_returned_fail',
      payload: {
        totalCount: '0',
        error: {
          errorCode: failedResults?.errorCode || null,
          errorMessage: failedResults?.errorMessage || 'No Results Found',
        },
      },
    },
  };

  window.ixisDataLayer.push(clone(event));
};

export const tier3ListOfDealers: Tier3DealersListSender = (configFile) => {
  const event: Event<Tier3DealersEventBody> = {
    vtp: {
      type: 'inventory_dealer_set',
      payload: configFile,
    },
  };

  window.ixisDataLayer.push(clone(event));
};
